import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { fmtErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Badge, PRIORITY_STYLE, STATUS_STYLE, PRIORITIES, STATUSES, CATEGORIES } from "../../components/DeskLayout";
import { toast } from "sonner";
import { MonitorPlay, ExternalLink, Copy, Tag, X, BookOpen } from "lucide-react";

const inputCls = "bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-900 outline-none";
const RTC_CONFIG = { iceServers: [{ urls: "stun:stun.l.google.com:19302" }] };

export default function TicketDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAgent = user?.role === "hd_admin" || user?.role === "hd_agent" || user?.role === "super_admin";
  const [ticket, setTicket] = useState(null);
  const [members, setMembers] = useState([]);
  const [company, setCompany] = useState(null);
  const [comment, setComment] = useState({ body: "", is_work_note: false });
  const [session, setSession] = useState(null);
  const [viewing, setViewing] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [resolveModal, setResolveModal] = useState(null);
  const [holdModal, setHoldModal] = useState(null);
  const [assets, setAssets] = useState([]);
  const [kbList, setKbList] = useState([]);
  const [kbPick, setKbPick] = useState("");
  const videoRef = useRef(null);
  const pcRef = useRef(null);
  const pollRef = useRef(null);

  const load = useCallback(() => {
    api.get(`/helpdesk/tickets/${id}`).then((r) => setTicket(r.data)).catch((e) => toast.error(fmtErr(e)));
  }, [id]);
  useEffect(() => {
    load();
    api.get("/helpdesk/company").then((r) => setCompany(r.data)).catch(() => {});
    if (isAgent) {
      api.get("/helpdesk/members").then((r) => setMembers(r.data.filter((m) => m.role !== "hd_user"))).catch(() => {});
      api.get("/helpdesk/kb").then((r) => setKbList(r.data)).catch(() => {});
      api.get("/helpdesk/assets").then((r) => setAssets(r.data)).catch(() => {});
    }
  }, [load, isAgent]);
  useEffect(() => () => { clearInterval(pollRef.current); pcRef.current?.close(); }, []);

  const update = async (field, value) => {
    if (field === "status" && ["resolved", "closed"].includes(value)) {
      setResolveModal({ status: value, code: company?.resolution_codes?.[0] || "", notes: ticket.resolution_notes || "" });
      return;
    }
    if (field === "status" && value === "on_hold") {
      setHoldModal({ reason: company?.hold_reasons?.[0] || "Awaiting Caller" });
      return;
    }
    try {
      const { data } = await api.put(`/helpdesk/tickets/${id}`, { [field]: value });
      setTicket((t) => ({ ...data, comments: t.comments }));
      toast.success("Ticket updated");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const putHold = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put(`/helpdesk/tickets/${id}`, { status: "on_hold", hold_reason: holdModal.reason });
      setTicket((t) => ({ ...data, comments: t.comments }));
      setHoldModal(null);
      toast.success(`On hold — ${holdModal.reason}`);
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const resolve = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put(`/helpdesk/tickets/${id}`, {
        status: resolveModal.status, resolution_code: resolveModal.code, resolution_notes: resolveModal.notes });
      setTicket((t) => ({ ...data, comments: t.comments }));
      setResolveModal(null);
      toast.success(`Ticket ${resolveModal.status} — ${resolveModal.code}`);
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const addTag = async (e) => {
    e.preventDefault();
    const tag = tagInput.trim();
    if (!tag) return;
    setTagInput("");
    await update("tags", [...(ticket.tags || []), tag]);
  };

  const removeTag = (tag) => update("tags", (ticket.tags || []).filter((t) => t !== tag));

  const attachKb = async () => {
    if (!kbPick) return;
    try {
      await api.post(`/helpdesk/tickets/${id}/kb`, { article_id: kbPick });
      setKbPick("");
      toast.success("Article attached");
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const detachKb = async (aid) => {
    try { await api.delete(`/helpdesk/tickets/${id}/kb/${aid}`); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const postComment = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/helpdesk/tickets/${id}/comments`, comment);
      setComment({ body: "", is_work_note: false });
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const startSession = async () => {
    try {
      const { data } = await api.post(`/helpdesk/tickets/${id}/share-session`);
      setSession(data);
      const link = `${window.location.origin}/desk/share/${data.id}`;
      await navigator.clipboard.writeText(link).catch(() => {});
      toast.success("Session created — share link copied. Send it to the user, then click Watch.");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const watch = async () => {
    if (!session) return;
    setViewing(true);
    const pc = new RTCPeerConnection(RTC_CONFIG);
    pcRef.current = pc;
    pc.ontrack = (e) => { if (videoRef.current) videoRef.current.srcObject = e.streams[0]; };
    pc.onicecandidate = (e) => e.candidate && api.post(`/helpdesk/share/${session.id}/candidate`, { side: "viewer", candidate: e.candidate.toJSON() });
    let answered = false, seen = 0;
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/helpdesk/share/${session.id}/state`);
        if (data.offer && !answered) {
          answered = true;
          await pc.setRemoteDescription(data.offer);
          const answer = await pc.createAnswer();
          await pc.setLocalDescription(answer);
          await api.post(`/helpdesk/share/${session.id}/answer`, { sdp: { type: answer.type, sdp: answer.sdp } });
        }
        for (const c of (data.sharer_candidates || []).slice(seen)) { seen++; await pc.addIceCandidate(c).catch(() => {}); }
        if (data.status === "ended") { clearInterval(pollRef.current); setViewing(false); pc.close(); }
      } catch { /* keep polling */ }
    }, 1200);
  };

  if (!ticket) return <p className="text-neutral-400 text-sm">Loading...</p>;
  const shareLink = session ? `${window.location.origin}/desk/share/${session.id}` : "";

  return (
    <div data-testid="ticket-detail-page">
      <Link to="/desk/tickets" className="text-sm text-sky-700 hover:underline">← Tickets</Link>
      <div className="flex flex-wrap items-center gap-3 mt-2 mb-1">
        <h1 className="text-2xl font-bold font-heading" data-testid="ticket-number">{ticket.number}</h1>
        <Badge value={ticket.priority} map={PRIORITY_STYLE} testId="ticket-priority-badge" />
        <Badge value={ticket.status} map={STATUS_STYLE} testId="ticket-status-badge" />
      </div>
      <p className="text-lg font-medium mb-4" data-testid="ticket-title">{ticket.title}</p>

      {isAgent ? (
        <div className="flex items-center gap-1 mb-4 overflow-x-auto" data-testid="ticket-state-flow">
          {STATUSES.map((s, i) => {
            const idx = STATUSES.indexOf(ticket.status);
            const active = s === ticket.status;
            return (
              <button key={s} data-testid={`state-step-${s}`} onClick={() => !active && update("status", s)}
                title={`Move to ${s.replace(/_/g, " ")}`}
                className={`flex items-center gap-2 px-4 py-2 text-xs font-semibold whitespace-nowrap transition-colors border border-neutral-200 ${
                  active ? "bg-[#0B1F33] text-white" : i < idx ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-white text-neutral-500 hover:bg-neutral-100"} ${
                  i === 0 ? "rounded-l-full" : ""} ${i === STATUSES.length - 1 ? "rounded-r-full" : ""}`}>
                <span className={`w-4 h-4 rounded-full text-[9px] flex items-center justify-center ${active ? "bg-white text-[#0B1F33]" : "bg-neutral-200 text-neutral-600"}`}>{i + 1}</span>
                {s.replace(/_/g, " ")}
              </button>
            );
          })}
          {["resolved", "closed"].includes(ticket.status) && (
            <button data-testid="agent-reopen-button" onClick={async () => {
              try { const { data } = await api.post(`/helpdesk/tickets/${id}/reopen`); setTicket((t) => ({ ...data, comments: t.comments })); load(); toast.success("Ticket reopened"); }
              catch (e) { toast.error(fmtErr(e)); }
            }} className="ml-2 px-3 py-1.5 rounded-full border border-amber-300 text-amber-700 text-xs font-medium hover:bg-amber-50">Reopen</button>
          )}
        </div>
      ) : (
        ticket.status === "resolved" && (
          <button data-testid="reopen-ticket-button" onClick={async () => {
            try { const { data } = await api.post(`/helpdesk/tickets/${id}/reopen`); setTicket((t) => ({ ...data, comments: t.comments })); load(); toast.success("Ticket reopened"); }
            catch (e) { toast.error(fmtErr(e)); }
          }} className="mb-4 px-4 py-2 rounded-md border border-amber-300 text-amber-700 text-sm font-medium hover:bg-amber-50">
            Not fixed? Reopen this ticket
          </button>
        )
      )}
      {ticket.status === "on_hold" && ticket.hold_reason && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4 inline-block" data-testid="hold-reason-badge">On hold — {ticket.hold_reason}</p>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-neutral-200 rounded-2xl p-6">
            <h3 className="font-semibold font-heading text-sm mb-2">Description</h3>
            <p className="text-sm text-neutral-700 whitespace-pre-wrap">{ticket.description || "—"}</p>
            <div className="flex flex-wrap items-center gap-1.5 mt-4" data-testid="ticket-tags">
              <Tag size={13} className="text-neutral-400" />
              {(ticket.tags || []).map((t) => (
                <span key={t} data-testid={`ticket-tag-${t}`} className="inline-flex items-center gap-1 text-xs bg-sky-50 text-sky-700 border border-sky-200 rounded-full px-2.5 py-0.5">
                  {t}
                  {isAgent && <button onClick={() => removeTag(t)} data-testid={`remove-tag-${t}`} className="hover:text-sky-900"><X size={11} /></button>}
                </span>
              ))}
              {(ticket.tags || []).length === 0 && !isAgent && <span className="text-xs text-neutral-400">no tags</span>}
              {isAgent && (
                <form onSubmit={addTag} className="inline-flex">
                  <input data-testid="tag-input" value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="+ add tag"
                    className="w-24 text-xs bg-transparent border-b border-dashed border-neutral-300 focus:border-neutral-900 outline-none px-1 py-0.5" />
                </form>
              )}
            </div>
            {ticket.resolution_code && (
              <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3" data-testid="ticket-resolution">
                <p className="text-xs font-semibold text-emerald-800">Resolution: {ticket.resolution_code}</p>
                {ticket.resolution_notes && <p className="text-xs text-emerald-700 mt-1 whitespace-pre-wrap">{ticket.resolution_notes}</p>}
              </div>
            )}
            {(ticket.kb_articles || []).length > 0 && (
              <div className="mt-4" data-testid="ticket-kb-list">
                <p className="text-xs font-semibold text-neutral-500 mb-1.5">Attached knowledge articles</p>
                {(ticket.kb_articles || []).map((a) => (
                  <div key={a.id} className="inline-flex items-center gap-1.5 text-xs bg-neutral-50 border border-neutral-200 rounded-full px-3 py-1 mr-1.5 mb-1.5" data-testid={`ticket-kb-${a.id}`}>
                    <BookOpen size={11} className="text-sky-600" />
                    <Link to="/desk/kb" className="hover:underline">{a.title}</Link>
                    {isAgent && <button onClick={() => detachKb(a.id)} data-testid={`detach-kb-${a.id}`} className="text-neutral-400 hover:text-neutral-900"><X size={11} /></button>}
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-neutral-400 mt-4">
              Requested by {isAgent && ticket.requester_email
                ? <Link to={`/desk/crm?q=${encodeURIComponent(ticket.requester_email)}`} data-testid="ticket-crm-link" className="text-sky-700 hover:underline font-medium">{ticket.requester_name}</Link>
                : ticket.requester_name} · {new Date(ticket.created_at).toLocaleString()} · SLA due {new Date(ticket.sla_due).toLocaleString()}
              {new Date(ticket.sla_due) < new Date() && !["resolved", "closed"].includes(ticket.status) && <span className="ml-2 text-red-600 font-semibold" data-testid="sla-breached-flag">SLA BREACHED</span>}
            </p>
          </div>

          <div className="bg-white border border-neutral-200 rounded-2xl p-6">
            <h3 className="font-semibold font-heading text-sm mb-4">Activity</h3>
            <div className="space-y-3 mb-5">
              {ticket.comments.length === 0 && <p className="text-sm text-neutral-400">No comments yet.</p>}
              {ticket.comments.map((c) => (
                <div key={c.id} data-testid={`comment-${c.id}`}
                  className={`rounded-xl px-4 py-3 text-sm border ${c.is_work_note ? "bg-amber-50 border-amber-200" : "bg-neutral-50 border-neutral-200"}`}>
                  <p className="whitespace-pre-wrap">{c.body}</p>
                  <p className="text-xs text-neutral-400 mt-1.5">{c.author_name} · {new Date(c.created_at).toLocaleString()}{c.is_work_note && " · work note (internal)"}</p>
                </div>
              ))}
            </div>
            <form onSubmit={postComment}>
              <textarea data-testid="comment-input" required rows={3} value={comment.body} placeholder="Write a comment..."
                onChange={(e) => setComment((c) => ({ ...c, body: e.target.value }))} className={inputCls + " w-full"} />
              <div className="flex items-center justify-between mt-2">
                {isAgent ? (
                  <label className="text-xs text-neutral-500 flex items-center gap-1.5">
                    <input data-testid="work-note-checkbox" type="checkbox" checked={comment.is_work_note}
                      onChange={(e) => setComment((c) => ({ ...c, is_work_note: e.target.checked }))} />
                    Work note (hidden from requester)
                  </label>
                ) : <span />}
                <button data-testid="comment-submit" className="px-5 py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium">Post</button>
              </div>
            </form>
          </div>
        </div>

        <div className="space-y-6">
          {isAgent && (
            <div className="bg-white border border-neutral-200 rounded-2xl p-6 space-y-3">
              <h3 className="font-semibold font-heading text-sm">Manage</h3>
              <div><label className="text-xs font-medium text-neutral-500">Status</label>
                <select data-testid="ticket-status-select" value={ticket.status} onChange={(e) => update("status", e.target.value)} className={inputCls + " w-full mt-1"}>
                  {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                </select></div>
              <div><label className="text-xs font-medium text-neutral-500">Priority</label>
                <select data-testid="ticket-priority-select" value={ticket.priority} onChange={(e) => update("priority", e.target.value)} className={inputCls + " w-full mt-1"}>
                  {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select></div>
              <div><label className="text-xs font-medium text-neutral-500">Category</label>
                <select data-testid="ticket-category-select" value={ticket.category} onChange={(e) => update("category", e.target.value)} className={inputCls + " w-full mt-1"}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select></div>
              <div><label className="text-xs font-medium text-neutral-500">Assignee</label>
                <select data-testid="ticket-assignee-select" value={ticket.assignee_id || ""} onChange={(e) => update("assignee_id", e.target.value)} className={inputCls + " w-full mt-1"}>
                  <option value="">Unassigned</option>
                  {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select></div>
              <div><label className="text-xs font-medium text-neutral-500">Affected asset</label>
                <select data-testid="ticket-asset-select" value={ticket.asset_id || ""} onChange={(e) => update("asset_id", e.target.value)} className={inputCls + " w-full mt-1"}>
                  <option value="">None</option>
                  {assets.map((a) => <option key={a.id} value={a.id}>{a.name}{a.tag ? ` (${a.tag})` : ""}</option>)}
                </select></div>
              <div><label className="text-xs font-medium text-neutral-500">Attach knowledge article</label>
                <div className="flex gap-1.5 mt-1">
                  <select data-testid="kb-attach-select" value={kbPick} onChange={(e) => setKbPick(e.target.value)} className={inputCls + " flex-1 min-w-0"}>
                    <option value="">Choose article...</option>
                    {kbList.filter((a) => !(ticket.kb_articles || []).some((x) => x.id === a.id)).map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
                  </select>
                  <button type="button" data-testid="kb-attach-button" onClick={attachKb} disabled={!kbPick}
                    className="px-3 rounded-md border border-neutral-300 text-xs font-medium hover:bg-neutral-50 disabled:opacity-40">Attach</button>
                </div></div>
            </div>
          )}

          <div className="bg-white border border-neutral-200 rounded-2xl p-6" data-testid="remote-support-card">
            <h3 className="font-semibold font-heading text-sm flex items-center gap-2 mb-3"><MonitorPlay size={16} /> Remote support</h3>
            {!session ? (
              <button data-testid="start-share-session" onClick={startSession}
                className="w-full py-2 rounded-md text-white bg-sky-600 hover:bg-sky-700 text-sm font-medium">Start screen share session</button>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-neutral-500">Send this link to the person who should share their screen:</p>
                <div className="flex gap-1.5">
                  <input readOnly value={shareLink} data-testid="share-link" className={inputCls + " flex-1 text-xs"} />
                  <button onClick={() => { navigator.clipboard.writeText(shareLink); toast.success("Link copied"); }}
                    className="px-2.5 border border-neutral-300 rounded-md hover:bg-neutral-50" data-testid="copy-share-link"><Copy size={14} /></button>
                </div>
                <button data-testid="watch-share-button" onClick={watch} disabled={viewing}
                  className="w-full py-2 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium disabled:opacity-50">
                  {viewing ? "Watching — waiting for stream..." : "Watch shared screen"}
                </button>
              </div>
            )}
            {viewing && (
              <video ref={videoRef} autoPlay playsInline muted data-testid="share-viewer-video"
                className="mt-3 w-full rounded-lg border border-neutral-200 bg-black aspect-video" />
            )}
            {company?.remote_tool_link && (
              <a href={company.remote_tool_link} target="_blank" rel="noreferrer" data-testid="remote-tool-link"
                className="mt-3 inline-flex items-center gap-1.5 text-xs text-sky-700 hover:underline">
                <ExternalLink size={12} /> Open remote tool ({new URL(company.remote_tool_link).hostname})
              </a>
            )}
          </div>
        </div>
      </div>

      {holdModal && (
        <div className="fixed inset-0 bg-neutral-900/40 z-50 flex items-center justify-center px-4" data-testid="hold-modal">
          <form onSubmit={putHold} className="bg-white rounded-2xl border border-neutral-200 shadow-xl max-w-sm w-full p-7">
            <h3 className="font-semibold font-heading text-lg mb-1">Place on hold</h3>
            <p className="text-xs text-neutral-400 mb-4">A hold reason is required.</p>
            <select data-testid="hold-reason-select" required value={holdModal.reason}
              onChange={(e) => setHoldModal({ reason: e.target.value })} className={inputCls + " w-full"}>
              {(company?.hold_reasons || []).map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <div className="flex gap-2 mt-5">
              <button data-testid="hold-confirm-button" className="flex-1 py-2.5 rounded-md text-white bg-amber-600 hover:bg-amber-700 text-sm font-medium">Confirm hold</button>
              <button type="button" onClick={() => setHoldModal(null)} className="px-5 py-2.5 rounded-md border border-neutral-300 text-sm font-medium hover:bg-neutral-50">Cancel</button>
            </div>
          </form>
        </div>
      )}
      {resolveModal && (
        <div className="fixed inset-0 bg-neutral-900/40 z-50 flex items-center justify-center px-4" data-testid="resolve-modal">
          <form onSubmit={resolve} className="bg-white rounded-2xl border border-neutral-200 shadow-xl max-w-md w-full p-7">
            <h3 className="font-semibold font-heading text-lg mb-1">Mark as {resolveModal.status}</h3>
            <p className="text-xs text-neutral-400 mb-4">A resolution code is required before this ticket can be {resolveModal.status}.</p>
            <label className="text-sm font-medium text-neutral-700">Resolution code</label>
            <select data-testid="resolution-code-select" required value={resolveModal.code}
              onChange={(e) => setResolveModal((m) => ({ ...m, code: e.target.value }))} className={inputCls + " w-full mt-1"}>
              {(company?.resolution_codes || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <label className="text-sm font-medium text-neutral-700 mt-3 block">Resolution notes (optional)</label>
            <textarea data-testid="resolution-notes-input" rows={3} value={resolveModal.notes}
              onChange={(e) => setResolveModal((m) => ({ ...m, notes: e.target.value }))} className={inputCls + " w-full mt-1"} />
            <div className="flex gap-2 mt-5">
              <button data-testid="resolve-confirm-button" className="flex-1 py-2.5 rounded-md text-white bg-emerald-700 hover:bg-emerald-800 text-sm font-medium">
                Confirm {resolveModal.status}
              </button>
              <button type="button" data-testid="resolve-cancel-button" onClick={() => setResolveModal(null)}
                className="px-5 py-2.5 rounded-md border border-neutral-300 text-sm font-medium hover:bg-neutral-50">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
